import React, { useEffect, useMemo, useState } from "react";
import {
  withScriptjs,
  withGoogleMap,
  GoogleMap,
  Marker,
  Polyline,
} from "react-google-maps";
import { Box, Stack, Typography, Alert, Skeleton, Button } from "@mui/material";
import { MdRefresh } from "react-icons/md";

import { Surface, SectionTitle } from "components/ui";
import { T, tint } from "theme/tokens";
import * as api from "../api";

const MAPS_KEY =
  process.env.REACT_APP_GOOGLE_MAPS_KEY ||
  "AIzaSyBjgK-bezlAjIF6PUj_239Ln9jYSaO_A30";

const MAP_URL = `https://maps.googleapis.com/maps/api/js?key=${MAPS_KEY}&libraries=geometry,drawing,places`;

const HYDERABAD = { lat: 17.4374837, lng: 78.5158477 };

const MAP_STYLES = [
  { featureType: "all", elementType: "geometry", stylers: [{ color: "#f4f6f6" }] },
  { featureType: "water", elementType: "geometry", stylers: [{ color: "#dce8e8" }] },
  { featureType: "road", elementType: "geometry", stylers: [{ color: "#ffffff" }] },
  { featureType: "poi", elementType: "labels", stylers: [{ visibility: "off" }] },
];

const when = (value) => {
  if (!value) return "unknown time";
  const d = new Date(value);
  return Number.isNaN(d.getTime())
    ? "unknown time"
    : d.toLocaleString("en-IN", {
        day: "2-digit",
        month: "short",
        hour: "2-digit",
        minute: "2-digit",
      });
};

const Canvas = withScriptjs(
  withGoogleMap(({ current, trail, center }) => (
    <GoogleMap
      defaultZoom={13}
      defaultCenter={center}
      options={{
        styles: MAP_STYLES,
        mapTypeControl: false,
        streetViewControl: false,
        fullscreenControl: true,
      }}
    >
      {/* The path is drawn oldest-to-newest so the line reads in the
          direction the partner actually travelled. */}
      {trail.length > 1 && (
        <Polyline
          path={[...trail].reverse().map((p) => ({ lat: p.lat, lng: p.lng }))}
          options={{ strokeColor: T.secondary, strokeOpacity: 0.7, strokeWeight: 3 }}
        />
      )}

      {trail.slice(1).map((point, index) => (
        <Marker
          key={`${point.lat}-${point.lng}-${index}`}
          position={{ lat: point.lat, lng: point.lng }}
          title={when(point.at)}
          icon={{
            path: window.google.maps.SymbolPath.CIRCLE,
            scale: 4,
            fillColor: T.secondary,
            fillOpacity: 0.5,
            strokeColor: "#ffffff",
            strokeWeight: 1,
          }}
        />
      ))}

      {current && (
        <Marker
          position={current}
          title="Last known position"
          zIndex={999}
          icon={{
            path: window.google.maps.SymbolPath.CIRCLE,
            scale: 10,
            fillColor: T.success,
            fillOpacity: 1,
            strokeColor: "#ffffff",
            strokeWeight: 3,
          }}
        />
      )}
    </GoogleMap>
  ))
);

/**
 * Where this partner has been.
 *
 * The partner document holds one point with no timestamp on it, so "last
 * known" is all it can honestly be called — the ping that set it could be
 * from two minutes or two months ago. LocationLog is the only thing that can
 * say when, which is why the trail is loaded alongside rather than instead.
 */
export default function LocationTab({ partnerId }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const load = () => {
    setLoading(true);
    setError("");
    api
      .getLocation(partnerId, { limit: 200 })
      .then(setData)
      .catch((err) =>
        setError(err?.response?.data?.message || "Could not load location history.")
      )
      .finally(() => setLoading(false));
  };

  useEffect(load, [partnerId]);

  // Memoised rather than derived inline: `data?.trail || []` builds a new
  // array on every render, which would re-run the centre calculation — and
  // remount the map — each time.
  const trail = useMemo(() => data?.trail || [], [data]);
  const current = data?.current || null;

  const center = useMemo(() => {
    if (current) return current;
    if (trail.length) return { lat: trail[0].lat, lng: trail[0].lng };
    return HYDERABAD;
  }, [current, trail]);

  if (loading) {
    return <Skeleton variant="rounded" height={520} sx={{ borderRadius: 3 }} />;
  }

  if (error) return <Alert severity="error">{error}</Alert>;

  if (!current && !trail.length) {
    return (
      <Surface sx={{ p: 5, textAlign: "center" }}>
        <Typography sx={{ color: T.muted, fontWeight: 600 }}>
          This partner has never reported a position.
        </Typography>
        <Typography sx={{ color: T.subtle, fontSize: "0.83rem", mt: 0.6 }}>
          Positions come from the partner app while it is signed in.
        </Typography>
      </Surface>
    );
  }

  return (
    <Stack spacing={2}>
      <Surface sx={{ p: 2 }}>
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          flexWrap="wrap"
          gap={1}
        >
          <Box>
            <SectionTitle sx={{ mb: 0.4 }}>Last known position</SectionTitle>
            <Typography sx={{ fontSize: "0.85rem", color: T.ink, fontFamily: "monospace" }}>
              {current
                ? `${current.lat.toFixed(5)}, ${current.lng.toFixed(5)}`
                : "Not set on the partner record"}
            </Typography>
            {/* Said out loud, because a dot on a map reads as "now" unless
                something tells you otherwise. */}
            <Typography sx={{ fontSize: "0.76rem", color: T.subtle, mt: 0.3 }}>
              {trail.length
                ? `Most recent ping ${when(trail[0].at)} · ${trail.length} point${
                    trail.length === 1 ? "" : "s"
                  } in the trail${data?.truncated ? " (capped)" : ""}`
                : "No timestamped history — the stored point could be any age"}
            </Typography>
          </Box>

          <Stack direction="row" spacing={1}>
            {current && (
              <Button
                size="small"
                href={`https://www.google.com/maps/search/?api=1&query=${current.lat},${current.lng}`}
                target="_blank"
                rel="noopener noreferrer"
                sx={{ textTransform: "none", color: T.secondary }}
              >
                Open in Google Maps
              </Button>
            )}
            <Button
              size="small"
              startIcon={<MdRefresh />}
              onClick={load}
              sx={{ textTransform: "none", color: T.primary }}
            >
              Refresh
            </Button>
          </Stack>
        </Stack>
      </Surface>

      <Box
        sx={{
          height: 460,
          borderRadius: 3,
          overflow: "hidden",
          border: `1px solid ${T.border}`,
        }}
      >
        <Canvas
          googleMapURL={MAP_URL}
          loadingElement={<div style={{ height: "100%" }} />}
          containerElement={<div style={{ height: "100%", width: "100%" }} />}
          mapElement={<div style={{ height: "100%" }} />}
          current={current}
          trail={trail}
          center={center}
        />
      </Box>

      <Surface sx={{ p: 1.5, bgcolor: tint(T.info, 0.05), borderColor: tint(T.info, 0.3) }}>
        <Typography sx={{ fontSize: "0.8rem", color: T.ink }}>
          The large green dot is the position stored on the partner record. The
          faint trail behind it is the timestamped history, newest first.
        </Typography>
      </Surface>
    </Stack>
  );
}
